'use client'

import { useState } from 'react'
import { History, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useBillingPlanHistoryQuery } from '@/hooks/queries/billing-plan-history'
import type { PlanHistoryEntry } from '@/lib/billing/plans/plan-history'
import type { PlanOperation } from '@/lib/billing/plans/plan-write'
import { describePlanChanges, formatPlanTimestamp } from './change-format'
import { ChangeList } from './change-list'
import { PlanDetails } from './plan-details'

export function PlanHistoryButton({ planId }: { planId: string }) {
  const [open, setOpen] = useState(false)
  return <>
    <Button type="button" variant="outline" onClick={() => setOpen(true)}>
      <History className="mr-2 h-4 w-4" />History
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Plan history</DialogTitle>
          <DialogDescription>Changes saved by administrators, newest first.</DialogDescription>
        </DialogHeader>
        {open ? <Timeline planId={planId} /> : null}
      </DialogContent>
    </Dialog>
  </>
}

function Timeline({ planId }: { planId: string }) {
  const { data, error, isPending } = useBillingPlanHistoryQuery(planId)
  if (isPending) {
    // Own element: the loaded states reuse this <p> (browser translation).
    return <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /><span>Loading history…</span></p>
  }
  if (error) return <p role="alert" className="text-sm text-destructive">{error.message || 'Could not load plan history.'}</p>
  if (data.entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No recorded changes yet. Changes made before history tracking started are not available.</p>
  }
  return (
    <div className="space-y-4">
      <ol className="space-y-5 border-l pl-5">
        {data.entries.map(entry => <Entry key={entry.id} entry={entry} />)}
      </ol>
      {data.truncated ? <p className="text-xs text-muted-foreground">Only the most recent changes are shown.</p> : null}
    </div>
  )
}

function Entry({ entry }: { entry: PlanHistoryEntry }) {
  const rows = describePlanChanges(entry.changes)
  return (
    <li className="relative min-w-0 space-y-3">
      <span aria-hidden="true" className="absolute -left-[1.6rem] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <Badge variant="secondary">{operationLabel(entry.operation)}</Badge>
        <time dateTime={entry.createdAt}>{formatPlanTimestamp(entry.createdAt)}</time>
        <span className="min-w-0 break-all text-muted-foreground">{`By ${entry.actorName}${entry.actorEmail ? ` (${entry.actorEmail})` : ''}`}</span>
      </div>
      {entry.operation === 'create'
        ? <details className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-semibold">Initial settings</summary>
          <div className="mt-3"><PlanDetails rows={rows.map(row => ({ ...row, value: row.to, inactive: false }))} /></div>
        </details>
        : <ChangeList rows={rows} />}
    </li>
  )
}

function operationLabel(operation: PlanOperation): string {
  switch (operation) {
    case 'create': return 'Created'
    case 'update': return 'Updated'
    case 'archive': return 'Archived'
    case 'restore': return 'Restored'
  }
}
